/**
 * The Clerk `<SignIn />` widget renders its own step headings, but the auth
 * screens hide them (see `authClerkAppearance`) so the page owns the heading
 * instead. That means the page has to track which step Clerk is on — otherwise
 * the second-factor step still reads "Welcome back / Enter your credentials",
 * which tells the user nothing about the code they are being asked for.
 */
export interface SignInStepCopy {
  title: string;
  description: string;
}

const CREDENTIALS_COPY: SignInStepCopy = {
  title: "Welcome back",
  description: "Enter your credentials to access your account",
};

const AUTHENTICATOR_APP_COPY: SignInStepCopy = {
  title: "Two-factor authentication",
  description:
    "Open your authenticator app and enter the 6-digit code it shows for A Safer Risk.",
};

const PHONE_CODE_COPY: SignInStepCopy = {
  title: "Two-factor authentication",
  description: "Enter the verification code we sent to your phone.",
};

/** Structural subset of Clerk's sign-in resource that drives the heading. */
export interface SignInStepState {
  status?: string | null;
  supportedSecondFactors?: readonly { strategy: string }[] | null;
}

export function getSignInStepCopy(signIn: SignInStepState | null | undefined): SignInStepCopy {
  if (signIn?.status !== "needs_second_factor") return CREDENTIALS_COPY;

  const strategies = signIn.supportedSecondFactors ?? [];
  // An authenticator app is the only second factor this app lets users enrol,
  // so it wins whenever it is on offer; `backup_code` is always available
  // alongside another factor and never describes the primary prompt.
  const hasAuthenticatorApp = strategies.some((factor) => factor.strategy === "totp");
  if (hasAuthenticatorApp || strategies.length === 0) return AUTHENTICATOR_APP_COPY;

  const hasPhoneCode = strategies.some((factor) => factor.strategy === "phone_code");
  return hasPhoneCode ? PHONE_CODE_COPY : AUTHENTICATOR_APP_COPY;
}
